import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Folder } from "lucide-react";
import { api, photoSrc } from "@/lib/api";
import { Reveal, SectionTag, SectionHeading } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";

const SPANS = [
  "md:col-span-2 md:row-span-2",
  "",
  "md:row-span-2",
  "",
  "md:col-span-2",
  "",
  "",
  "md:col-span-2",
];

export const PhotoLibrarySection = ({ onEnquire }) => {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/photos")
      .then((r) => setPhotos(r.data))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const [albumCovers, setAlbumCovers] = useState({});

  useEffect(() => {
    api
      .get("/albums")
      .then((r) => {
        const map = {};
        r.data.forEach((a) => {
          map[a.name] = a.cover_photo_id;
        });
        setAlbumCovers(map);
      })
      .catch(() => {});
  }, []);

  const albums = useMemo(() => {
    const map = new Map();
    photos.forEach((p) => {
      if (!p.album) return;
      if (!map.has(p.album)) map.set(p.album, { name: p.album, count: 0, cover: p });
      map.get(p.album).count += 1;
    });
    return [...map.values()].map((a) => {
      const coverPhoto = albumCovers[a.name] ? photos.find((p) => p.id === albumCovers[a.name]) : null;
      return coverPhoto ? { ...a, cover: coverPhoto } : a;
    });
  }, [photos, albumCovers]);

  const sports = useMemo(() => ["All", ...new Set(photos.map((p) => p.sport))], [photos]);
  const visible = photos.filter(
    (p) =>
      (filter === "All" || p.sport === filter) &&
      (activeAlbum ? p.album === activeAlbum : !p.album || p.show_outside)
  );

  return (
    <section id="library" data-testid="photo-library-section" className="px-4 sm:px-8 lg:px-16 py-16 lg:py-28 border-t border-white/10">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <SectionHeading>
            {activeAlbum ? (
              activeAlbum
            ) : (
              <>
                Photo <span className="text-[var(--ekal-accent)]">Library</span>
              </>
            )}
          </SectionHeading>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
            {visible.length} frames · licensing available
          </p>
        </div>
      </Reveal>

      {activeAlbum && (
        <button
          data-testid="album-back-btn"
          onClick={() => setActiveAlbum(null)}
          className="mb-8 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300 transition-colors"
        >
          <ArrowLeft size={14} /> All frames
        </button>
      )}

      {!activeAlbum && albums.length > 0 && (
        <Reveal delay={0.05}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-12">
            {albums.map((a) => (
              <button
                key={a.name}
                data-testid={`album-card-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setActiveAlbum(a.name)}
                className="group relative h-36 sm:h-44 overflow-hidden border border-white/10 hover:border-amber-400/60 text-left transition-colors"
              >
                <img
                  src={photoSrc(a.cover)}
                  alt={a.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-500 group-hover:opacity-80 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-black/10" />
                <div className="absolute bottom-0 inset-x-0 p-4">
                  <p className="flex items-center gap-2 font-heading font-bold uppercase tracking-tight text-xl leading-none">
                    <Folder size={16} className="text-amber-400 shrink-0" /> {a.name}
                  </p>
                  <p className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-300">
                    {a.count} frames — open folder
                  </p>
                </div>
              </button>
            ))}
          </div>
        </Reveal>
      )}

      <Reveal delay={0.1}>
        <div className="flex flex-wrap gap-2 mb-10">
          {sports.map((s) => (
            <button
              key={s}
              data-testid={`gallery-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(s)}
              className={`text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 border transition-colors ${
                filter === s
                  ? "border-amber-400 bg-amber-400 text-[var(--ekal-bg)]"
                  : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Reveal>

      {loading ? (
        <div className="py-20" data-testid="gallery-loading" aria-hidden="true" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] sm:auto-rows-[210px] gap-3">
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.05, 0.3)} className={SPANS[i % SPANS.length]}>
              <button
                data-testid={`gallery-photo-card-${p.id}`}
                onClick={() => setActive(p)}
                className="group relative block h-full w-full overflow-hidden border border-white/10 hover:border-amber-400/60 transition-colors text-left"
              >
                <img
                  src={photoSrc(p)}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 inset-x-0 p-4 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <p className="font-heading font-bold uppercase tracking-tight text-lg leading-none">{p.title}</p>
                </div>
              </button>
            </Reveal>
          ))}
          {visible.length === 0 && (
            <p className="text-sm text-slate-500 font-mono uppercase tracking-[0.2em] py-16 col-span-full text-center">
              No frames in this discipline yet.
            </p>
          )}
        </div>
      )}

      <Lightbox photo={active} onClose={() => setActive(null)} onEnquire={onEnquire} />
    </section>
  );
};
